import useSeti from '@/hooks/useSeti'
import pclnExperimentation from '@pcln/experimentation'
import { formatPackageSearchHref } from '@pcln/horizon-search-form'
import { Box, Link, Text } from 'pcln-design-system'
import { useEffect, useRef } from 'react'
import {
  trackCustomPackageSearchPromotionClick,
  trackCustomPackageSearchPromotionView,
  trackPackageSearchLinkClick,
  trackPackageSearchLinkView,
} from '../ga4'
import type { MakeWatchObject, PackagesFormStateType } from '../types'
import { isHotelPlusCar } from './TripTypes'

type AdvancedSearchLinkProps = Readonly<{
  pageName: 'homepage' | 'landing'
  watchState: MakeWatchObject<PackagesFormStateType>
}>

function AdvancedSearchLinkFlight({ pageName, watchState }: AdvancedSearchLinkProps) {
  const {
    watchTripType,
    watchStartLocation,
    watchEndLocation,
    watchStartDate,
    watchEndDate,
    watchTravelers,
    watchHotelStartDate,
    watchHotelEndDate,
    watchPartialStay,
  } = watchState
  const isCustomPackageSearchCopySeti =
    useSeti({
      experimentCode: 'PKG_ADVANCED_SEARCH_COPY_UPDATE',
      shouldFireImpression: false,
    }) === 'VARIANT'
  const hasFiredCopyUpdateImpressionRef = useRef(false)
  useEffect(() => {
    trackPackageSearchLinkView(pageName, watchTripType)
  }, [pageName, watchTripType])
  useEffect(() => {
    if (!hasFiredCopyUpdateImpressionRef.current) {
      hasFiredCopyUpdateImpressionRef.current = true
      pclnExperimentation.fireImpression('PKG_ADVANCED_SEARCH_COPY_UPDATE')
      if (isCustomPackageSearchCopySeti) {
        trackCustomPackageSearchPromotionView(pageName)
      }
    }
  }, [pageName, isCustomPackageSearchCopySeti])
  return (
    <Box>
      <Link
        href={formatPackageSearchHref(
          pageName,
          watchTripType,
          watchStartLocation,
          watchEndLocation,
          watchStartDate,
          watchEndDate,
          watchTravelers,
          watchHotelStartDate,
          watchHotelEndDate,
          watchPartialStay,
        )}
        target='_blank'
        onClick={() => {
          trackPackageSearchLinkClick(pageName, watchTripType)
          if (isCustomPackageSearchCopySeti) {
            trackCustomPackageSearchPromotionClick(pageName)
          }
        }}
      >
        <Text fontSize='14px' fontWeight='bold'>
          {isCustomPackageSearchCopySeti
            ? 'Custom Package Search (One-Way Flight, Multi-Hotel)'
            : 'Advanced Search (One-Way Flight, Multi-Hotel)'}
        </Text>
      </Link>
    </Box>
  )
}

function AdvancedSearchLinkHotelCar({ pageName, watchState }: AdvancedSearchLinkProps) {
  const {
    watchTripType,
    watchStartLocation,
    watchEndLocation,
    watchStartDate,
    watchEndDate,
    watchTravelers,
    watchHotelStartDate,
    watchHotelEndDate,
    watchPartialStay,
  } = watchState
  const advancedSearchHotelCarLinkSeti =
    useSeti({
      experimentCode: 'PKG_HP_ADVANCED_SEARCH_HC',
      shouldFireImpression: false,
    }) === 'VARIANT'
  const isCustomPackageSearchCopySeti =
    useSeti({
      experimentCode: 'PKG_ADVANCED_SEARCH_COPY_UPDATE',
      shouldFireImpression: false,
    }) === 'VARIANT'
  const hasFiredCopyUpdateImpressionRef = useRef(false)
  useEffect(() => {
    pclnExperimentation.fireImpression('PKG_HP_ADVANCED_SEARCH_HC')
  }, [])
  useEffect(() => {
    if (advancedSearchHotelCarLinkSeti) {
      trackPackageSearchLinkView(pageName, watchTripType)
    }
  }, [pageName, watchTripType, advancedSearchHotelCarLinkSeti])
  useEffect(() => {
    if (advancedSearchHotelCarLinkSeti && !hasFiredCopyUpdateImpressionRef.current) {
      hasFiredCopyUpdateImpressionRef.current = true
      pclnExperimentation.fireImpression('PKG_ADVANCED_SEARCH_COPY_UPDATE')
      if (isCustomPackageSearchCopySeti) {
        trackCustomPackageSearchPromotionView(pageName)
      }
    }
  }, [pageName, advancedSearchHotelCarLinkSeti, isCustomPackageSearchCopySeti])
  return (
    advancedSearchHotelCarLinkSeti && (
      <Box>
        <Link
          href={formatPackageSearchHref(
            pageName,
            watchTripType,
            watchStartLocation,
            watchEndLocation,
            watchStartDate,
            watchEndDate,
            watchTravelers,
            watchHotelStartDate,
            watchHotelEndDate,
            watchPartialStay,
          )}
          target='_blank'
          onClick={() => {
            trackPackageSearchLinkClick(pageName, watchTripType)
            if (isCustomPackageSearchCopySeti) {
              trackCustomPackageSearchPromotionClick(pageName)
            }
          }}
        >
          <Text fontSize='14px' fontWeight='bold'>
            {isCustomPackageSearchCopySeti
              ? 'Custom Package Search (Multi-Hotel, Custom Car Dates)'
              : 'Advanced Search (Multi-Hotel, Custom Car Dates)'}
          </Text>
        </Link>
      </Box>
    )
  )
}

export default function AdvancedSearchLink({ pageName, watchState }: AdvancedSearchLinkProps) {
  const { watchTripType } = watchState
  const showAdvancedSearchLinkHotelCar = isHotelPlusCar(watchTripType)
  return (
    <>
      {watchTripType.includes('FLY') && (
        <AdvancedSearchLinkFlight pageName={pageName} watchState={watchState} />
      )}
      {showAdvancedSearchLinkHotelCar && (
        <AdvancedSearchLinkHotelCar pageName={pageName} watchState={watchState} />
      )}
    </>
  )
}
